import _ from 'lodash'
import qs from 'qs'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { CreateStaffDto, Staff } from './staff.model'
import { CreateEstablishmentDto, Establishment, UpdateEstablishmentDto } from './establishment.model'
import { SearchRequestOptions } from '@/core/types/request'
import { EstablishmentSubscription, SubscriptionType } from '@/utils/subscription/types'
import { Account } from './account.model'
import { KeycloakUserType, UserInfo } from '@/core/types/shared'

export type Admin = Account & {
  photo?: string
  emailStatistics: string[]
}

export type CreateAdminDto = {
  email: string
  establishment: string
  userInfo: UserInfo
  type: KeycloakUserType
}

export type CreatePlugin = {
  apiUrl: string
  apiKey: string
  pluginUrl: string
}

export type CountByWeek = {
  year: number
  week: number
  count: number
}

export type CountByString = {
  key: string
  count: number
}

export type RankCount = {
  name: string
  surname: string
  email: string
  count: number
}
export type DailyStatus = {
  date: string;
  data: Array<{ status: string, count: number }>;
};

export type WeeklyConsultations = {
  startDate: string;
  endDate: string;
  data: Array<{ day: string, completedCount: number, scheduledCount: number }>;
};

export type DailyPatients = {
  date: string;
  data: Array<{ day: string, newPatientsCount: number }>;
};

export type WeeklyConsultationsByType = {
  startDate: string;
  endDate: string;
  data: Array<{ day: string, type: string, count: number }>;
};

export type DailyConsultationsReasons = {
  date: string;
  data: Array<{ reason: string, count: number }>;
};

export type WeeklySpecialtyConsultations = {
  startDate: string;
  endDate: string;
  data: Array<{ day: string, specialty: string, count: number }>;
};


export type AdminState = CommonState & {
  establishments: EntityState<Establishment, Establishment['_id']>
  staffs: EntityState<Staff, Staff['_id']>
}

class AdminModel extends Model {
  static instance: AdminModel

  static getInstance() {
    if (!AdminModel.instance) {
      AdminModel.instance = new AdminModel('admin')
    }
    return AdminModel.instance
  }

  getInitialState(): AdminState {
    return _.merge({}, this.commonState, {
      establishments: { entities: {}, ids: [] },
      staffs: { entities: {}, ids: [] },
    })
  }

  createAdmin = async (createAdminDto: CreateAdminDto): Promise<Admin> => {
    return await this.httpClient.request<Admin>('/admin', 'POST', createAdminDto)
  }

  updateSuperAdmin = async (emails: string[]): Promise<Admin> => {
    return await this.httpClient.request<Admin>('/super-admin', 'PATCH', emails)
  }

  createEstablishment = async (establishmentDto: CreateEstablishmentDto): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>('/admin/etablissements', 'POST', establishmentDto)
  }

  deleteEstablishment = async (establishmentId: string): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/etablissements/${establishmentId}`, 'DELETE')
  }

  getEstablishments = async (): Promise<Establishment[]> => {
    return await this.httpClient.request<Establishment[]>('/admin/etablissements', 'GET')
  }

  getEstablishment = async (): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>('/admin/etablissement', 'GET')
  }

  // returns staff from req.user.id
  getStaffs = async (filter: Record<string, any>, options?: SearchRequestOptions): Promise<[Staff[], number]> => {
    return await this.httpClient.request<[Staff[], number]>(`/admin/staffs?${qs.stringify({ ...filter, ...options })}`, 'GET')
  }

  deleteStaff = async (staffId: string, associatedData?: string[]): Promise<Staff> => {
    const queryString = !associatedData || !associatedData.length ? '' : `?${qs.stringify({ associatedData })}`
    return await this.httpClient.request<Staff>(`/admin/staffs/${staffId}${queryString}`, 'DELETE')
  }

  addStaff = async (staff: CreateStaffDto): Promise<Staff> => {
    return await this.httpClient.request<Staff>(`/admin/staffs`, 'POST', staff)
  }

  addPlugin = async (plugin: CreatePlugin): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/plugins`, 'POST', plugin)
  }

  deletePlugin = async (plugin: CreatePlugin): Promise<CreatePlugin> => {
    return await this.httpClient.request<CreatePlugin>(`/admin/plugins`, 'DELETE', plugin)
  }

  testPlugin = async (plugin: CreatePlugin): Promise<boolean> => {
    return await this.httpClient.request<boolean>(`/plugin/ping?apiurl=${plugin.apiUrl}&apikey=${plugin.apiKey}&pluginurl=${plugin.pluginUrl}`, 'GET')
  }

  getConsultationsByWeek = async (etablissementId: string): Promise<CountByWeek[]> => {
    return await this.httpClient.request<CountByWeek[]>(`/admin/etablissements/${etablissementId}/statistics/consultations`, 'GET')
  }

  getPatientsByWeek = async (etablissementId: string): Promise<CountByWeek[]> => {
    return await this.httpClient.request<CountByWeek[]>(`/admin/etablissements/${etablissementId}/statistics/patients`, 'GET')
  }

  getConsultationsByStatus = async (etablissementId: string): Promise<CountByString[]> => {
    return await this.httpClient.request<CountByString[]>(`/admin/etablissements/${etablissementId}/statistics/consultations/status`, 'GET')
  }

  getConsultationsByType = async (etablissementId: string): Promise<CountByString[]> => {
    return await this.httpClient.request<CountByString[]>(`/admin/etablissements/${etablissementId}/statistics/consultations/type`, 'GET')
  }

  getConsultationsByStaff = async (etablissementId: string): Promise<RankCount[]> => {
    return await this.httpClient.request<RankCount[]>(`/admin/etablissements/${etablissementId}/statistics/rank`, 'GET')
  }

  updateEstablishment = async (etablissementId: string, updateEstablishmentDto: UpdateEstablishmentDto): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/etablissements/${etablissementId}`, 'PATCH', updateEstablishmentDto)
  }

  updateEstablishmentSubscription = async (etablissementId: string, subscription: EstablishmentSubscription): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/etablissements/${etablissementId}/subscriptions`, 'PATCH', { subscription })
  }

  updateStaffSubscription = async (staffId: string, subscription: SubscriptionType): Promise<Staff> => {
    return await this.httpClient.request<Staff>(`/staffs/${staffId}/subscription`, 'PATCH', { subscription })
  }

  createOrdoclicAccount = async (staffId: string) => {
    return await this.httpClient.request<Staff>(`/admin/staffs/${staffId}/accounts/ordoclic`, 'POST')
  }

  getDailyStatus = async (etablissementId: string, date: string): Promise<Array<{ status: string, count: number }>> => {
    return await this.httpClient.request<Array<{ status: string, count: number }>>(`/admin/etablissements/${etablissementId}/statistics/daily/daily-status`, 'GET', { date });
  }

  getWeeklyConsultations = async (etablissementId: string, startDate: string, endDate: string): Promise<Array<{ day: string, completedCount: number, scheduledCount: number }>> => {
    return await this.httpClient.request<Array<{ day: string, completedCount: number, scheduledCount: number }>>(`/admin/etablissements/${etablissementId}/statistics/weekly/weekly-consultations`, 'GET', { startDate, endDate });
  }

  getDailyPatients = async (etablissementId: string, date: string): Promise<Array<{ day: string, newPatientsCount: number }>> => {
    return await this.httpClient.request<Array<{ day: string, newPatientsCount: number }>>(`/admin/etablissements/${etablissementId}/statistics/daily/daily-new-patients`, 'GET', { date });
  }

  getWeeklyConsultationsByType = async (etablissementId: string, startDate: string, endDate: string): Promise<Array<{ day: string, type: string, count: number }>> => {
    return await this.httpClient.request<Array<{ day: string, type: string, count: number }>>(`/admin/etablissements/${etablissementId}/statistics/weekly/weekly-type`, 'GET', { startDate, endDate });
  }

  getDailyConsultationsReasons = async (etablissementId: string, date: string): Promise<Array<{ reason: string, count: number }>> => {
    return await this.httpClient.request<Array<{ reason: string, count: number }>>(`/admin/etablissements/${etablissementId}/statistics/daily/daily-reasons`, 'GET', { date });
  }

  getWeeklySpecialtyConsultations = async (etablissementId: string, startDate: string, endDate: string): Promise<Array<{ day: string, specialty: string, count: number }>> => {
    return await this.httpClient.request<Array<{ day: string, specialty: string, count: number }>>(`/admin/etablissements/${etablissementId}/statistics/weekly/weekly-specialty`, 'GET', { startDate, endDate });
  }

  createOrdoclicEntity = async (establishmentId: string, data: { name: string; finess?: string }): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/etablissements/${establishmentId}/ordoclic`, 'POST', data);
  }

  assignProfessionalsToOrdoclicEntity = async (establishmentId: string): Promise<Establishment> => {
    return await this.httpClient.request<Establishment>(`/admin/etablissements/${establishmentId}/ordoclic/assign-all-professionals`, 'POST');
  }
}

export default AdminModel.getInstance()
